// @flow

import { graphql, useFragment } from 'react-relay';
import { useLocale } from '../../hooks/locale';
import type { TranslateFn } from '../../controls/locale';
import type { CountryCode } from '../../server/UserData';
import { useUserData } from '../../hooks/user-data';
import { createDescriptor } from '../../controls/router';
import {
  offerTypeDescriptor,
  roomsFilterDescriptor,
  propertyTypeFilterDescriptor,
} from './Filters/sharedDescriptors';

import type { useAggregatesListingsPlaceData_aggregatesListings$key } from './__generated__/useAggregatesListingsPlaceData_aggregatesListings.graphql';

const descriptor = createDescriptor(
  offerTypeDescriptor,
  propertyTypeFilterDescriptor,
  roomsFilterDescriptor,
);

type Props = {|
  aggregatesListings: ?useAggregatesListingsPlaceData_aggregatesListings$key,
|};

export type PlaceData = {|
  label: string,
  inLabel: string,
  slugEn: ?string,
  placeType:
    | 'country'
    | 'region'
    | 'state'
    | 'district'
    | 'municipality'
    | 'locality'
    | 'neighbourhood'
    | 'agglomeration'
    | 'postcode'
    | 'other',
  propertyType: null | string,
  offerType: 'buy' | 'rent',
  rooms?: null | {|
    noIndex: boolean, // if this is `true` we don't want the page to be indexed
    gte: number,
    lte?: ?number,
  |},
  seoRoomLinksMinRooms: ?number,
|};

const minRoomsByType = propertyType => {
  switch (propertyType) {
    case 'APPT':
      return 1;
    case 'HOUSE':
      return 4;
    default:
      return null;
  }
};

const getRooms = (state, placeWithIndexRooms) => {
  if (state.numberOfRooms_gte == null) {
    return null;
  }

  const gte = state.numberOfRooms_gte;
  const lte = state.numberOfRooms_lte;

  if (!placeWithIndexRooms) {
    return { gte, lte, noIndex: true };
  }

  if (lte != null) {
    if (Math.floor(lte - gte - 0.5) !== 0) {
      return { gte, lte, noIndex: true };
    }
  } else {
    if (gte !== 6) {
      return { gte, lte, noIndex: true };
    }
  }

  const minRooms = minRoomsByType(state.compositePropertyType_eq);
  if (minRooms != null && gte >= minRooms) {
    return { gte, lte, noIndex: false };
  }

  return { gte, lte, noIndex: true };
};

export const getCountryLabel = (
  t: TranslateFn,
  countryCode: CountryCode,
): string => {
  switch (countryCode) {
    case 'CH':
      return t('switzerland');
    case 'FR':
      return t('france');
    case 'ES':
      return t('spain');
    default:
      return t('switzerland');
  }
};

export const useAggregatesListingsPlaceData = (props: Props): PlaceData => {
  const aggregatesListings = useFragment(
    graphql`
      fragment useAggregatesListingsPlaceData_aggregatesListings on AggregatesListingConnectionConnection {
        filters {
          places {
            slugEn: slug(lang: en)
            type
            label
            inLabel
            indexRooms
          }
        }
      }
    `,
    props.aggregatesListings,
  );

  const { t } = useLocale();
  const { countryCode } = useUserData();
  const [state] = descriptor.useState();

  const { offerType, compositePropertyType_eq: propertyType } = state;

  const places = aggregatesListings?.filters.places ?? [];
  const placeWithIndexRooms = places.length === 1 && places[0].indexRooms;

  const baseParams = {
    propertyType,
    offerType,
    seoRoomLinksMinRooms: placeWithIndexRooms
      ? minRoomsByType(propertyType)
      : null,
    rooms: getRooms(state, placeWithIndexRooms),
    slugEn: places.length === 1 ? places[0].slugEn : null,
  };

  if (places.length === 0) {
    return {
      ...baseParams,
      placeType: 'country',
      label: getCountryLabel(t, countryCode),
      inLabel: getCountryLabel(t, countryCode),
    };
  }

  if (places.length === 1) {
    const place = places[0];
    return {
      ...baseParams,
      placeType: place.type,
      label: place.label,
      inLabel: place.inLabel,
    };
  }

  return {
    ...baseParams,
    placeType: 'other',
    label: places.map(place => place.label).join(', '),
    inLabel: '',
  };
};
